import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Label,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogActions,
  DialogContent,
  Radio,
  RadioGroup,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add16Filled } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { checkUserPermission } from "../../../../utils";
import { UserPermissionsList } from "../../../../config";
import ContentLoader from "../../../../components/Loader/ContentLoader";
import ASCButton from "../../../../components/Button";
import { ModuleLabel, radioOptions } from "../caseHelpers";
import {
  clearAllPoliciesData,
  getPolicyListItems,
} from "../../../../redux/Case/policySlice";
import NoDataFound from "../../../../components/NoDataFound/noDataFound";

const useStyles = makeStyles({
  field: {
    display: "grid",
    gap: tokens.spacingVerticalS,
    marginBottom: "20px",
  },
  wrapper: {
    display: "flex",
    justifyContent: "flex-end",
    margin: "10px 10px 0",
  },
});

const PolicyHomePage: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, action } = useParams<{
    clientId: string;
    action?: string;
  }>();

  const { authUser, policies } = useSelector((state: RootState) => ({
    authUser: state.authUser,
    policies: state.policies,
  }));

  const styles = useStyles();

  useEffect(() => {
    if (clientId) {
      dispatch(clearAllPoliciesData());
      dispatch(getPolicyListItems(clientId));
    }
  }, [clientId, dispatch]);

  useEffect(() => {
    if (policies.listItems.length > 0) {
      navigate(
        `/clients/${clientId}/policies/${
          policies.listItems[0]?.policy_id
        }/policy/${action || "view"}`
      );
    }
  }, [clientId, action, navigate, policies.listItems]);

  const handleAddCase = useCallback(() => {
    if (!selectedCaseType) return;
    setIsDialogOpen(false);
    navigate(`/clients/${clientId}/policies/add`, {
      state: { caseType: selectedCaseType },
    });
  }, [selectedCaseType, clientId, navigate]);

  return (
    <div>
      {policies.loading ? (
        <ContentLoader />
      ) : policies.listItems.length === 0 ? (
        <div>
          <div className={styles.wrapper}>
            {checkUserPermission(
              authUser,
              UserPermissionsList.ALLOW_UPDATE_CLIENT
            ) && (
              <ASCButton
                shape="rounded"
                appearance="primary"
                className="asc-button-primary"
                icon={<Add16Filled />}
                size="medium"
                onClick={() => setIsDialogOpen(true)}
                label="Add New"
              />
            )}
          </div>
          <NoDataFound />
        </div>
      ) : null}

      <Dialog open={isDialogOpen} modalType="modal">
        <DialogSurface>
          <DialogBody>
            <DialogContent>
              <div className={styles.field}>
                <Label weight="semibold">
                  Select {ModuleLabel["policies"]} Category
                </Label>
                <RadioGroup
                  value={selectedCaseType}
                  onChange={(_, data) => setSelectedCaseType(data.value)}
                >
                  {radioOptions["policies"]?.map((radio: any) => (
                    <Radio
                      key={radio.value}
                      value={radio.value}
                      label={radio.label}
                    />
                  ))}
                </RadioGroup>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <ASCButton
                  shape="rounded"
                  appearance="secondary"
                  size="medium"
                  onClick={() => setIsDialogOpen(false)}
                  label="Close"
                />
              </DialogTrigger>
              <ASCButton
                shape="rounded"
                appearance="primary"
                size="medium"
                className="asc-button-primary"
                onClick={handleAddCase}
                label="Submit"
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default PolicyHomePage;
